// src/billing/confirmContent.js — what a Piani confirmation says, per pending action (pure).
//
// `pending` is the page's open confirmation (see sections/Piani.jsx): { kind, option?, pack?, codes, discloses?,
// planName?, changed? }. Every line is OUR sentence (billing/disclosureCopy.js). `changed` = a re-confirm after
// the session answered with different codes.
// ⭐ `purchasedHeld` (ctx): does the company hold purchased credits (`/credits/balance.purchased_remaining > 0`)?
// The backend sends PURCHASED_CREDITS_KEPT without knowing; telling a company with no packs (or a negative pot,
// a debt) that its extra credits "stay available" is false, so `false` drops that sentence. `null` = not known
// (read pending or failed) → fail open, the sentence stays. Only the WORDING is filtered: `pending.codes` stays
// whole, so the re-confirm comparison (sameCodes) still sees what the backend sent.
// ⭐ v204 «Passa ora»: a `change` whose row is `change-now` carries `pending.preview` (POST /change-plan/preview) and
// is worded from it — the charge table (`charge`, rows from billing/money.js with our labels), the preview's codes
// with its total and card (CHARGED_TO_SAVED_CARD), and a button naming the total. No usable card (none, expired, or
// not a card) → `noCard: true`: no charge is offered, the button opens the card update instead.
// `quoteChanged` = a re-confirm after the quote expired or the total moved. ctx `currentPlanName` labels the unused
// part of the current plan; ctx `today` (formatted) dates the new plan's line.
import { disclosureLines, lossLines } from './disclosureCopy.js';
import { chargeRows, formatMinorUnits } from './money.js';
import { formatCredits } from '../utils/creditsFormat.js';

/** Order-sensitive equality of two code lists (a non-array counts as empty). */
export function sameCodes(a, b) {
  const x = Array.isArray(a) ? a : [];
  const y = Array.isArray(b) ? b : [];
  return x.length === y.length && x.every((code, i) => code === y[i]);
}

const unique = (lines) => [...new Set(lines)];

/** The card the preview says will be charged, or null when there is no usable one. */
const usableCard = (card) => (card && card.expired !== true ? card : null);

/** Does this «Passa ora» confirmation lack a usable card (so its button opens the card update, not a charge)? */
export function lacksUsableCard(pending) {
  return !!pending?.preview && !usableCard(pending.preview.card);
}

/** The charge table with our labels, or null when the preview carries no usable amount. */
function chargeTable(t, amount, { plan, currentPlanName, today }) {
  const rows = chargeRows(amount);
  if (!rows) return null;
  const label = {
    newPlan: () => t('confirm.amount.newPlan', { plan, date: today }),
    unused: () => (currentPlanName
      ? t('confirm.amount.unused', { plan: currentPlanName }) : t('confirm.amount.unusedNoName')),
    subtotal: () => t('confirm.amount.subtotal'),
    tax: () => t('confirm.amount.tax'),
    total: () => t('confirm.amount.total'),
  };
  return rows.map((r) => ({ ...r, label: label[r.id]() }));
}

/** «Passa ora» from its preview (see the header). */
function changeNowContent(t, pending, lead, ctx) {
  const plan = pending.option.display_name;
  const { amount, discloses } = pending.preview;
  const card = usableCard(pending.preview.card);
  const title = t('confirm.titles.change', { plan });
  if (!card) {
    return { title, lines: [t('confirm.noCard', { plan })], confirmLabel: t('current.updateCard'), charge: null, noCard: true };
  }
  const total = formatMinorUnits(amount?.total, amount?.currency);
  return {
    title,
    charge: chargeTable(t, amount, { plan, ...ctx }),
    lines: unique([
      ...(pending.quoteChanged ? [t('confirm.quoteChanged')] : []),
      ...lead,
      ...disclosureLines(t, pending.codes, {
        plan, interval: pending.option.billing_interval, charge: { total, brand: card.brand, last4: card.last4 },
      }),
      ...lossLines(t, discloses),
    ]),
    confirmLabel: t('confirm.actions.payAndChange', { total, plan }),
    noCard: false,
  };
}

export function confirmContent(t, pendingIn, { purchasedHeld = null, currentPlanName = null, today = '' } = {}) {
  const pending = purchasedHeld === false
    ? { ...pendingIn, codes: (pendingIn.codes || []).filter((code) => code !== 'PURCHASED_CREDITS_KEPT') }
    : pendingIn;
  const lead = pending.changed ? [t('confirm.changed')] : [];
  switch (pending.kind) {
    case 'checkout': {
      const plan = pending.option.display_name;
      return {
        title: t('confirm.titles.subscribe', { plan }),
        lines: unique([...lead, ...disclosureLines(t, pending.codes, { plan, interval: pending.option.billing_interval })]),
        confirmLabel: t('confirm.actions.toPayment'),
      };
    }
    case 'change': {
      if (pending.preview && pending.option.action === 'change-now') {
        return changeNowContent(t, pending, lead, { currentPlanName, today });
      }
      const plan = pending.option.display_name;
      const atRenewal = pending.option.action === 'change-at-renewal';
      return {
        title: t('confirm.titles.change', { plan }),
        lines: unique([
          ...lead,
          ...disclosureLines(t, pending.codes, { plan, interval: pending.option.billing_interval }),
          ...lossLines(t, pending.discloses),
        ]),
        confirmLabel: t(atRenewal ? 'confirm.actions.schedule' : 'plan.changeNow'),
      };
    }
    case 'pack':
      return {
        title: t('confirm.titles.pack', { credits: formatCredits(pending.pack.credits) }),
        lines: unique([...lead, ...disclosureLines(t, pending.codes)]),
        confirmLabel: t('confirm.actions.toPayment'),
      };
    case 'cancel':
      return {
        title: t('confirm.titles.cancel'),
        lines: unique([t('cancelConfirm'), ...disclosureLines(t, pending.codes)]),
        confirmLabel: t('confirm.actions.cancelSubscription'),
      };
    case 'resume':
      return { title: t('confirm.titles.resume'), lines: [t('confirm.resumeBody')], confirmLabel: t('current.resume') };
    case 'cancelChange':
      return {
        title: t('confirm.titles.cancelChange'),
        lines: [t('confirm.cancelChangeBody', { plan: pending.planName })],
        confirmLabel: t('current.cancelChange'),
      };
    default:
      return { title: t('confirm.title'), lines: [], confirmLabel: t('confirm.actions.confirm') };
  }
}
